import api from '../utils/api';
import { readSSE } from '../utils/sse';

// Auth Services
export const authService = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.data?.tokens?.accessToken) {
      localStorage.setItem('accessToken', response.data.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.data.tokens.refreshToken);
    }
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/auth/profile', data);
    return response.data.data;
  },

  setTheme: async (theme) => {
    const response = await api.put('/auth/theme', { theme });
    return response.data.data;
  },

  configureAPI: async (config) => {
    const response = await api.post('/auth/configure-api', config);
    return response.data.data;
  },

  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },
};

// Content Services
export const contentService = {
  // Program formats with a large duration/wordCount target (Burning Issue, TCR,
  // World News) can trigger several sequential continuation rounds server-side
  // (see ContentGenerationService.generateContent) to actually reach the target
  // — worst case (9000+ words) takes a few minutes. The shared default timeout
  // (240s) was tuned for single-shot generation and would abort a still-running
  // request early, which is exactly the "it just loads and returns nothing"
  // failure mode already solved the same way elsewhere in this file (see
  // generateThumbnailsAI). A longer timeout is harmless for every other content
  // type — it only matters if actually exceeded, and short generations still
  // return in seconds regardless of the ceiling.
  generateContent: async (data) => {
    const response = await api.post('/content/generate', data, { timeout: 600000 });
    return response.data.data;
  },

  generateBatchContent: async (data) => {
    const response = await api.post('/content/generate-batch', data);
    return response.data.data;
  },

  getHistory: async (filters = {}) => {
    const response = await api.get('/content/history', { params: filters });
    return response.data.data;
  },

  getReport: async (filters = {}) => {
    const response = await api.get('/content/report', { params: filters });
    return response.data.data;
  },

  getContentById: async (id) => {
    const response = await api.get(`/content/${id}`);
    return response.data.data;
  },

  editContent: async (id, data) => {
    const response = await api.put(`/content/${id}`, data);
    return response.data.data;
  },

  rateContent: async (id, rating) => {
    const response = await api.post(`/content/${id}/rate`, rating);
    return response.data.data;
  },

  getUserStats: async (timeframe = 'month') => {
    const response = await api.get('/content/stats', { params: { timeframe } });
    return response.data.data;
  },

  getEditorialRules: async () => {
    const response = await api.get('/content/rules/get');
    return response.data.data;
  },

  updateEditorialRules: async (rules) => {
    const response = await api.put('/content/rules/update', rules);
    return response.data.data;
  },

  resetEditorialRules: async () => {
    const response = await api.post('/content/rules/reset');
    return response.data.data;
  },

  downloadContent: async (data) => {
    const response = await api.post('/content/download', data, { responseType: 'blob' });
    return response.data;
  },

  saveEditFeedback: async (data) => {
    const response = await api.post('/content/save-edit', data);
    return response.data.data;
  },
};

// QuickAI Service — ChatGPT-style raw chat, no content-type prompt engineering.
// Thread CRUD goes through the shared axios instance like everything else;
// the actual generation call is a hand-rolled fetch() because it needs to read
// a streaming response body (SSE) as it arrives — axios doesn't expose that,
// and EventSource can't send a POST body / Authorization header.
export const quickAIService = {
  listThreads: async () => {
    const response = await api.get('/quickai/threads');
    return response.data.data;
  },

  getThread: async (id) => {
    const response = await api.get(`/quickai/threads/${id}`);
    return response.data.data;
  },

  deleteThread: async (id) => {
    const response = await api.delete(`/quickai/threads/${id}`);
    return response.data;
  },

  /** Admin/manager only — every in-scope user's threads (role-scoped server-side). */
  listThreadsForAdmin: async (filters = {}) => {
    const response = await api.get('/quickai/admin/threads', { params: filters });
    return response.data.data;
  },

  /** Admin/manager only — view one specific user's thread, read-only. */
  getThreadForAdmin: async (id) => {
    const response = await api.get(`/quickai/admin/threads/${id}`);
    return response.data.data;
  },

  /** An attached file, for <img> tags and links (the token goes in the URL). */
  fileUrl: (threadId, fileId) => {
    const base = api.defaults.baseURL || '/api';
    const token = encodeURIComponent(localStorage.getItem('accessToken') || '');
    return `${base}/quickai/threads/${threadId}/files/${fileId}?token=${token}`;
  },

  /**
   * Streams a QuickAI reply via SSE-over-fetch.
   *
   * @param {{threadId?: string, message: string, files?: File[]}} payload — files: up to 5 attachments, any kind
   * @param {{
   *   onThread?: (data: {threadId, title}) => void,
   *   onChunk?:  (text: string) => void,
   *   onDone?:   (data: {threadId, title}) => void,
   *   onError?:  (message: string) => void,
   *   signal?: AbortSignal,
   * }} handlers
   */
  streamMessage: async (payload, { onThread, onChunk, onDone, onError, signal } = {}) => {
    const base = api.defaults.baseURL || '/api';
    const token = localStorage.getItem('accessToken');

    // With files: a multipart form (the browser sets its Content-Type);
    // without: plain JSON, as before.
    const { files = [], ...fields } = payload;
    let body;
    if (files.length) {
      body = new FormData();
      if (fields.threadId) body.append('threadId', fields.threadId);
      body.append('message', fields.message || '');
      files.forEach((f) => body.append('files', f, f.name));
    } else {
      body = JSON.stringify(fields);
    }

    let response;
    try {
      response = await fetch(`${base}/quickai/stream`, {
        method: 'POST',
        headers: {
          ...(!files.length && { 'Content-Type': 'application/json' }),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body,
        signal,
      });
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
      // The browser's own wording ("Failed to fetch") says nothing to a user.
      onError?.('Could not reach the server — check your connection and try again.');
      return;
    }

    if (!response.ok || !response.body) {
      let message = `Request failed (${response.status}).`;
      try {
        const errJson = await response.json();
        message = errJson.message || message;
      } catch {
        // response wasn't JSON (e.g. proxy error page) — keep the generic message
      }
      onError?.(message);
      return;
    }

    // A stream that ends without 'done' or 'error' was cut off on the way
    // (proxy, network) — reported, so the page can fetch the saved reply
    // instead of showing a half answer as if it were finished.
    let settled = false;
    try {
      await readSSE(response.body, (eventName, data) => {
        if (eventName === 'thread') onThread?.(data);
        else if (eventName === 'chunk') onChunk?.(data.text);
        else if (eventName === 'done') { settled = true; onDone?.(data); }
        else if (eventName === 'error') { settled = true; onError?.(data.message); }
      });
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
      onError?.('The connection dropped before the reply finished.', { dropped: true });
      return;
    }
    if (!settled && !signal?.aborted) onError?.('The connection dropped before the reply finished.', { dropped: true });
  },
};

// POST `payload` and read the reply as server-sent events, calling
// handlers[eventName](data) for each one (same wire format as QuickAI's stream).
const streamEvents = async (path, payload, { signal, onError, ...handlers } = {}) => {
  const base = api.defaults.baseURL || '/api';
  const token = localStorage.getItem('accessToken');

  let response;
  try {
    response = await fetch(`${base}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (e) {
    if (e.name !== 'AbortError') onError?.({ message: e.message || 'Network error — could not reach the server.' });
    return;
  }

  if (!response.ok || !response.body) {
    let message = `Request failed (${response.status}).`;
    try {
      message = (await response.json()).message || message;
    } catch {
      // not JSON (e.g. a proxy error page) — keep the generic message
    }
    onError?.({ message, status: response.status });
    return;
  }

  // Ended without 'done' or 'error': cut off on the way — said so, instead of
  // leaving a half-written script looking finished.
  let settled = false;
  try {
    await readSSE(response.body, (name, data) => {
      if (name === 'done' || name === 'error') settled = true;
      if (name === 'error') onError?.(data);
      else handlers[`on${name[0].toUpperCase()}${name.slice(1)}`]?.(data);
    });
    if (!settled && !signal?.aborted) {
      onError?.({ message: 'The connection dropped before it finished — what arrived is shown. Please try again.', code: 'STREAM_DROPPED' });
    }
  } catch (e) {
    if (e.name !== 'AbortError') onError?.({ message: e.message || 'The connection dropped.', code: 'STREAM_DROPPED' });
  }
};

// Program tabs (Burning Issue, TCR, World News, Crime Story) — streamed script.
// handlers: onStatus({step}), onChunk({text}), onDone({contentId, enrichedFacts}), onError({message, code}), signal
export const programService = {
  stream: (payload, handlers) => streamEvents('/content/program/stream', payload, handlers),
};

// Full Story — script → voice-over → pictures → video. Every call resolves
// with the updated story (list → { items, total, page, pages }).
const fsData = (promise) => promise.then((r) => r.data.data);
const fsUpload = (url, file) => {
  const form = new FormData();
  form.append('file', file);
  return fsData(api.post(url, form, { timeout: 300000 }));
};
export const fullStoryService = {
  list: (params = {}) => fsData(api.get('/full-story', { params })),
  create: (data = {}) => fsData(api.post('/full-story', data)),
  options: () => fsData(api.get('/full-story/options')),
  get: (id) => fsData(api.get(`/full-story/${id}`)),
  update: (id, data) => fsData(api.patch(`/full-story/${id}`, data)),
  remove: (id) => fsData(api.delete(`/full-story/${id}`)),

  // handlers: onChunk({text}), onDone({story}), onError({message, code}), signal
  streamScript: (id, payload, handlers) => streamEvents(`/full-story/${id}/script/stream`, payload, handlers),
  saveScript: (id, text) => fsData(api.put(`/full-story/${id}/script`, { text })),
  restoreScript: (id, index) => fsData(api.post(`/full-story/${id}/script/restore`, { index })),
  approveScript: (id) => fsData(api.post(`/full-story/${id}/script/approve`)),

  generateVoice: (id, data) => fsData(api.post(`/full-story/${id}/voice`, data)),
  redoSentence: (id, sentenceId, data = {}) => fsData(api.post(`/full-story/${id}/voice/sentences/${sentenceId}`, data)),
  approveVoice: (id) => fsData(api.post(`/full-story/${id}/voice/approve`)),
  fitLength: (id) => fsData(api.post(`/full-story/${id}/voice/fit`, {}, { timeout: 180000 })),

  planScenes: (id, data = {}) => fsData(api.post(`/full-story/${id}/scenes/plan`, data)),           // { instructions? }
  remakePictures: (id, data = {}) => fsData(api.post(`/full-story/${id}/images/remake`, data)),     // { instructions? }
  setLayout: (id, layout) => fsData(api.put(`/full-story/${id}/scenes/layout`, { layout })),
  updateScene: (id, sceneId, data) => fsData(api.patch(`/full-story/${id}/scenes/${sceneId}`, data)),
  // { motion?, effect? } → { filename } of a short clip of the scene
  previewScene: (id, sceneId, data = {}) => fsData(api.post(`/full-story/${id}/scenes/${sceneId}/preview`, data, { timeout: 120000 })),
  generateSceneImage: (id, sceneId, data = {}) => fsData(api.post(`/full-story/${id}/scenes/${sceneId}/generate`, data)),
  searchScenePhotos: (id, sceneId, query) => fsData(api.post(`/full-story/${id}/scenes/${sceneId}/search`, { query }, { timeout: 120000 })),
  chooseScenePhoto: (id, sceneId, filename) => fsData(api.post(`/full-story/${id}/scenes/${sceneId}/choose`, { filename })),
  uploadSceneImage: (id, sceneId, file) => fsUpload(`/full-story/${id}/scenes/${sceneId}/upload`, file),
  // MP4 / MOV / WebM up to 200 MB; may be converted on the server, hence fsUpload's long timeout.
  uploadSceneVideo: (id, sceneId, file) => fsUpload(`/full-story/${id}/scenes/${sceneId}/upload-video`, file),
  removeSceneImage: (id, sceneId) => fsData(api.delete(`/full-story/${id}/scenes/${sceneId}/image`)),
  approveImages: (id) => fsData(api.post(`/full-story/${id}/images/approve`)),

  uploadOption: (id, kind, file) => fsUpload(`/full-story/${id}/options/${kind}`, file),
  updateOptions: (id, data) => fsData(api.patch(`/full-story/${id}/options`, data)),
  removeOption: (id, kind) => fsData(api.delete(`/full-story/${id}/options/${kind}`)),
  render: (id) => fsData(api.post(`/full-story/${id}/render`)),

  /** URL for a story file — the token goes in the query because <img>/<audio>/<video> can't send headers. */
  fileUrl: (id, filename, { download = false } = {}) => {
    const base = api.defaults.baseURL || '/api';
    const token = encodeURIComponent(localStorage.getItem('accessToken') || '');
    return `${base}/full-story/${id}/files/${filename}?token=${token}${download ? '&download=1' : ''}`;
  },
  voiceoverMp3Url: (id) => {
    const base = api.defaults.baseURL || '/api';
    return `${base}/full-story/${id}/voiceover.mp3?token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}`;
  },
};

// Full Story Video — Full Story from raw footage. Same method names as
// fullStoryService where the steps are shared (Voice, Video, effects).
const FSV = '/full-story-video';
const FSV_CHUNK_BYTES = 25 * 1024 * 1024;
export const fullStoryVideoService = {
  list: (params = {}) => fsData(api.get(FSV, { params })),
  create: (data = {}) => fsData(api.post(FSV, data)),
  options: () => fsData(api.get(`${FSV}/options`)),
  get: (id) => fsData(api.get(`${FSV}/${id}`)),
  update: (id, data) => fsData(api.patch(`${FSV}/${id}`, data)),
  remove: (id) => fsData(api.delete(`${FSV}/${id}`)),
  parse: (text) => fsData(api.post(`${FSV}/parse`, { text })),

  /**
   * Upload a (possibly 5 GB) video in 25 MB chunks, a few at a time, each
   * retried on a network error — a dropped connection costs one chunk, not
   * the whole upload. onProgress(fraction). Resolves with the story.
   */
  uploadSource: async (id, file, { onProgress = () => {}, signal } = {}) => {
    const started = await fsData(api.post(`${FSV}/${id}/sources`, { name: file.name, size: file.size, chunkSize: FSV_CHUNK_BYTES }));
    const total = started.upload.totalChunks;
    let done = 0;
    let next = 0;
    const sendOne = async (index) => {
      const blob = file.slice(index * FSV_CHUNK_BYTES, Math.min(file.size, (index + 1) * FSV_CHUNK_BYTES));
      for (let attempt = 1; ; attempt++) {
        if (signal?.aborted) throw new Error('Upload cancelled.');
        try {
          await api.put(`${FSV}/${id}/sources/${started._id}/chunks/${index}`, blob, {
            headers: { 'Content-Type': 'application/octet-stream' }, timeout: 300000, signal,
          });
          return;
        } catch (e) {
          if (attempt >= 5 || (e.response && e.response.status < 500 && e.response.status !== 429)) throw e;
          await new Promise((r) => setTimeout(r, 2000 * attempt));
        }
      }
    };
    const worker = async () => {
      while (next < total) {
        const index = next++;
        await sendOne(index);
        onProgress(++done / total);
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, total) }, worker));
    return fsData(api.post(`${FSV}/${id}/sources/${started._id}/complete`));
  },
  addSourceImage: (id, file) => fsUpload(`${FSV}/${id}/sources/images`, file),
  removeSource: (id, sourceId) => fsData(api.delete(`${FSV}/${id}/sources/${sourceId}`)),
  sourceDetail: (id, sourceId) => fsData(api.get(`${FSV}/${id}/sources/${sourceId}`)),

  streamScript: (id, payload, handlers) => streamEvents(`${FSV}/${id}/script/stream`, payload, handlers),
  saveScript: (id, data) => fsData(api.put(`${FSV}/${id}/script`, data)),        // { text?, input? }
  restoreScript: (id, index) => fsData(api.post(`${FSV}/${id}/script/restore`, { index })),
  approveScript: (id) => fsData(api.post(`${FSV}/${id}/script/approve`)),

  generateVoice: (id, data) => fsData(api.post(`${FSV}/${id}/voice`, data)),
  redoSentence: (id, sentenceId, data = {}) => fsData(api.post(`${FSV}/${id}/voice/sentences/${sentenceId}`, data)),
  approveVoice: (id) => fsData(api.post(`${FSV}/${id}/voice/approve`)),

  planScenes: (id, data = {}) => fsData(api.post(`${FSV}/${id}/scenes/plan`, data)),
  updateScene: (id, sceneId, data) => fsData(api.patch(`${FSV}/${id}/scenes/${sceneId}`, data)),
  removeScene: (id, sceneId) => fsData(api.delete(`${FSV}/${id}/scenes/${sceneId}`)),
  chooseFootage: (id, sceneId, data) => fsData(api.post(`${FSV}/${id}/scenes/${sceneId}/footage`, data)),
  previewScene: (id, sceneId, data = {}) => fsData(api.post(`${FSV}/${id}/scenes/${sceneId}/preview`, data, { timeout: 120000 })),
  generateSceneImage: (id, sceneId, data = {}) => fsData(api.post(`${FSV}/${id}/scenes/${sceneId}/generate`, data)),
  searchScenePhotos: (id, sceneId, query) => fsData(api.post(`${FSV}/${id}/scenes/${sceneId}/search`, { query }, { timeout: 120000 })),
  chooseScenePhoto: (id, sceneId, filename) => fsData(api.post(`${FSV}/${id}/scenes/${sceneId}/choose`, { filename })),
  uploadSceneImage: (id, sceneId, file) => fsUpload(`${FSV}/${id}/scenes/${sceneId}/upload`, file),
  approveImages: (id) => fsData(api.post(`${FSV}/${id}/images/approve`)),

  uploadOption: (id, kind, file) => fsUpload(`${FSV}/${id}/options/${kind}`, file),
  updateOptions: (id, data) => fsData(api.patch(`${FSV}/${id}/options`, data)),
  removeOption: (id, kind) => fsData(api.delete(`${FSV}/${id}/options/${kind}`)),
  render: (id) => fsData(api.post(`${FSV}/${id}/render`)),

  fileUrl: (id, filename, { download = false } = {}) => {
    const base = api.defaults.baseURL || '/api';
    const token = encodeURIComponent(localStorage.getItem('accessToken') || '');
    if (filename === 'voiceover.mp3') return `${base}${FSV}/${id}/voiceover.mp3?token=${token}`;
    return `${base}${FSV}/${id}/files/${filename}?token=${token}${download ? '&download=1' : ''}`;
  },
  voiceoverMp3Url: (id) => {
    const base = api.defaults.baseURL || '/api';
    return `${base}${FSV}/${id}/voiceover.mp3?token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}&download=1`;
  },
  // The shared library: a music track to listen to, an overlay frame's loop to preview.
  musicUrl: (trackId) => {
    const base = api.defaults.baseURL || '/api';
    return `${base}${FSV}/library/music/${trackId}?token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}`;
  },
  frameUrl: (frameId, aspect) => {
    const base = api.defaults.baseURL || '/api';
    return `${base}${FSV}/library/frames/${frameId}?aspect=${encodeURIComponent(aspect || '16:9')}&token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}`;
  },
  platePreviewUrl: (styleId, aspect) => {
    const base = api.defaults.baseURL || '/api';
    return `${base}${FSV}/library/plates/${styleId}?aspect=${encodeURIComponent(aspect || '16:9')}&token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}`;
  },
  // Graphic plate templates: the catalogue (with which loops are drawn), and a template's poster.jpg or loop.mp4.
  plateTemplates: () => fsData(api.get(`${FSV}/library/plate-templates`)),
  plateTemplatePreview: (templateId) => fsData(api.get(`${FSV}/library/plate-templates/${templateId}/preview`)),
  // A prompt (required) and up to 5 reference pictures (optional).
  // tags (optional): a list of words that help find the plate.
  createPlateTemplate: (prompt, { name, category, tags = [], images = [] } = {}) => {
    if (!images.length) return fsData(api.post(`${FSV}/library/plate-templates`, { prompt, name, category, tags }));
    const form = new FormData();
    form.append('prompt', prompt);
    if (name) form.append('name', name);
    if (category) form.append('category', category);
    if (tags.length) form.append('tags', tags.join(','));
    images.forEach((file) => form.append('images', file));
    return fsData(api.post(`${FSV}/library/plate-templates`, form));
  },
  removePlateTemplate: (templateId) => fsData(api.delete(`${FSV}/library/plate-templates/${templateId}`)),
  // The channel's own plates: a 16:9 picture with an empty text panel.
  uploadPlateTemplate: (file, { name, category } = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (name) form.append('name', name);
    if (category) form.append('category', category);
    return fsData(api.post(`${FSV}/library/plate-templates/upload`, form));
  },
  updatePlateTemplate: (templateId, changes) => fsData(api.patch(`${FSV}/library/plate-templates/${templateId}`, changes)),
  // Fonts for uploaded plates' words, and each one's sample (its name written in it).
  plateFonts: () => fsData(api.get(`${FSV}/library/plate-fonts`)),
  // A font for everyone (Unicode Telugu, checked by the server): .ttf, .otf or .ttc.
  uploadPlateFont: (file, name = '') => {
    const form = new FormData();
    form.append('file', file);
    if (name) form.append('name', name);
    return fsData(api.post(`${FSV}/library/plate-fonts`, form));
  },
  removePlateFont: (uploadId) => fsData(api.delete(`${FSV}/library/plate-fonts/${uploadId}`)),
  plateTemplatePrompt: (templateId) => fsData(api.get(`${FSV}/library/plate-templates/${templateId}/prompt`)),
  // Write (again) an uploaded plate's prompt — in the background; the gallery shows it when it's there.
  writePlatePrompt: (templateId) => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/prompt`)),
  // A created plate's thread: ask for a change (a new version), or show an earlier version again.
  editCreatedPlate: (templateId, instruction, images = []) => {
    const form = new FormData();
    form.append('instruction', instruction);
    images.forEach((file) => form.append('images', file));
    return fsData(api.post(`${FSV}/library/plate-templates/${templateId}/edits`, form));
  },
  // An uploaded or created plate to play live in its viewer: words (sample words in its panel), and a look to try (motion, dust, fx, transition).
  // A look being edited, in full (text area, ink, font, style, blur areas, motion, effects, transition, people): sent in the body.
  plateLiveLook: (templateId, look = {}) => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/live`, look)),
  plateLive: (templateId, { words = false, motion, dust, fx, transition, people } = {}) => fsData(api.get(`${FSV}/library/plate-templates/${templateId}/live`, {
    params: {
      words: words ? 1 : undefined, motion, dust: dust === undefined ? undefined : (dust ? 1 : 0),
      fx: Array.isArray(fx) ? fx.join(',') : fx, transition,
      // How its people move, to try: { motion, entrance, strength, enabled }.
      ...(people ? {
        peopleMotion: people.motion, peopleEntrance: people.entrance, peopleStrength: people.strength,
        people: people.enabled === undefined ? undefined : (people.enabled ? 1 : 0),
      } : {}),
    },
  })),
  // Find a plate's people, so they move (in the background, about a minute); hint: who to find.
  startPlatePeople: (templateId, hint = '') => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/people`, hint ? { hint } : {})),
  // Its people: who was found, who moves, a job running.
  platePeople: (templateId) => fsData(api.get(`${FSV}/library/plate-templates/${templateId}/people`)),
  // Who moves ({ selected: [ids] }) and the brush's marks ({ paint: PNG data URL | null }): drawn at once.
  updatePlatePeople: (templateId, change) => fsData(api.put(`${FSV}/library/plate-templates/${templateId}/people`, change)),
  // A person or object to move, painted over ({ area: PNG data URL, label }): in the background.
  addPlatePeopleSubject: (templateId, body) => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/people/subjects`, body)),
  removePlatePeopleSubject: (templateId, subjectId) => fsData(api.delete(`${FSV}/library/plate-templates/${templateId}/people/subjects/${subjectId}`)),
  // Fill in the background behind what moves now (in the background).
  // { method: 'ai' (in the background; hint: what is behind) | 'extend' (at once, no AI) }.
  fillPlatePeople: (templateId, body = {}) => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/people/fill`, body)),
  // An exact outline ({ mask: PNG data URL, label, fill: extend | ai | none, hint }): no "Find people" needed.
  addPlatePeopleExact: (templateId, body) => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/people/exact`, body)),
  // The channel's own clean plate (the design without them) as the background behind them.
  uploadPlatePeopleClean: (templateId, file) => {
    const form = new FormData();
    form.append('file', file);
    return fsData(api.post(`${FSV}/library/plate-templates/${templateId}/people/clean`, form));
  },
  // 3D: studios everyone can stand plates in, and settings from a description / reference.
  plateStudios: () => fsData(api.get(`${FSV}/library/plate-studios`)),
  uploadPlateStudio: (file, name = '') => {
    const form = new FormData();
    form.append('file', file);
    if (name) form.append('name', name);
    return fsData(api.post(`${FSV}/library/plate-studios`, form));
  },
  createPlateStudio: (prompt, name = '') => fsData(api.post(`${FSV}/library/plate-studios/ai`, { prompt, name }, { timeout: 300000 })),
  removePlateStudio: (studioId) => fsData(api.delete(`${FSV}/library/plate-studios/${studioId}`)),
  plateStudioUrl: (studioId) => {
    const base = api.defaults.baseURL || '/api';
    return `${base}${FSV}/library/plate-studios/${studioId}/picture.jpg?token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}`;
  },
  // { prompt, from, to, current, file (a clip or a picture) } → { settings, summary, summary_te, sheet }.
  suggestPlateStage3d: (templateId, { prompt = '', from = '', to = '', current = null, file = null } = {}) => {
    const form = new FormData();
    form.append('prompt', prompt);
    if (from !== '') form.append('from', String(from));
    if (to !== '') form.append('to', String(to));
    if (current) form.append('current', JSON.stringify(current));
    if (file) form.append('file', file);
    return fsData(api.post(`${FSV}/library/plate-templates/${templateId}/stage3d/suggest`, form, { timeout: 300000 }));
  },
  clearPlatePeopleTask: (templateId) => fsData(api.delete(`${FSV}/library/plate-templates/${templateId}/people/task`)),
  retryCreatedPlate: (templateId) => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/retry`)),
  restorePlateVersion: (templateId, n) => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/versions/${n}/restore`)),
  plateFontSampleUrl: (fontId) => {
    const base = api.defaults.baseURL || '/api';
    return `${base}${FSV}/library/plate-fonts/${fontId}/sample.png?token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}`;
  },
  // `version` (an uploaded plate's updatedAt) makes the browser fetch a changed plate afresh.
  // download: served as a file to save (background.jpg, fullhd.jpg).
  plateTemplateUrl: (templateId, kind, version, { download = false } = {}) => {
    const base = api.defaults.baseURL || '/api';
    const v = version ? `&v=${encodeURIComponent(version)}` : '';
    return `${base}${FSV}/library/plate-templates/${templateId}/${kind}?token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}${v}${download ? '&download=1' : ''}`;
  },
  // Graphic plate prompts to use again: the user's favourites (labels) and the latest anyone made a plate with.
  platePrompts: () => fsData(api.get(`${FSV}/library/plate-prompts`)),
  addPlatePromptFavorite: (plateId, label) => fsData(api.post(`${FSV}/library/plate-prompts/favorites`, { plateId, label })),
  relabelPlatePromptFavorite: (id, label) => fsData(api.put(`${FSV}/library/plate-prompts/favorites/${id}`, { label })),
  removePlatePromptFavorite: (id) => fsData(api.delete(`${FSV}/library/plate-prompts/favorites/${id}`)),
  platePromptImageUrl: (id, version) => {
    const base = api.defaults.baseURL || '/api';
    const v = version ? `&v=${encodeURIComponent(version)}` : '';
    return `${base}${FSV}/library/plate-prompts/favorites/${id}/image.jpg?token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}${v}`;
  },
  // A plate's tags (they help search).
  setPlateTags: (templateId, tags) => fsData(api.put(`${FSV}/library/plate-templates/${templateId}/tags`, { tags })),
  // Packages: one plate, one or two text areas, up to 40 texts.
  platePackages: (templateId) => fsData(api.get(`${FSV}/library/plate-packages`, { params: { plate: templateId } })),
  savePlatePackage: (templateId, pkg, packageId = null) => fsData(packageId
    ? api.put(`${FSV}/library/plate-templates/${templateId}/packages/${packageId}`, pkg)
    : api.post(`${FSV}/library/plate-templates/${templateId}/packages`, pkg)),
  removePlatePackage: (packageId) => fsData(api.delete(`${FSV}/library/plate-packages/${packageId}`)),
  // One plate of a package as a picture (a Blob, JPEG): { areas, texts, font, blurs, plateBlurs, noText }.
  platePackagePreview: async (templateId, body, { signal } = {}) => (await api.post(
    `${FSV}/library/plate-templates/${templateId}/package-preview`, body, { responseType: 'blob', signal },
  )).data,
  // Saved text styles (how a text area's words look, to reuse on any plate) and the most used colours: { styles, usedColors }.
  plateTextStyles: () => fsData(api.get(`${FSV}/library/plate-text-styles`)),
  savePlateTextStyle: (style, styleId = null) => fsData(styleId
    ? api.put(`${FSV}/library/plate-text-styles/${styleId}`, style)
    : api.post(`${FSV}/library/plate-text-styles`, style)),
  removePlateTextStyle: (styleId) => fsData(api.delete(`${FSV}/library/plate-text-styles/${styleId}`)),
  // A package's plates to play live in the browser (no video is made): { size, background, spec, stage, plates }.
  // look: the plate's motion, effects, transition, people and blur areas as being edited (not saved yet).
  // full: every picture at 1920×1080 (to make the download video in the browser).
  platePackageLive: (templateId, pkg, items = null, look = null, { full = false } = {}) => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/package-live`, { package: pkg, ...(items ? { items } : {}), ...(look ? { look } : {}), ...(full ? { full: true } : {}) }, full ? { timeout: 120000 } : undefined)),
  // Downloads that take a while: start, follow, then save from plateExportUrl.
  startPlateExport: (templateId, body) => fsData(api.post(`${FSV}/library/plate-templates/${templateId}/exports`, body)),
  plateExport: (jobId) => fsData(api.get(`${FSV}/library/plate-exports/${jobId}`)),
  plateExportUrl: (jobId) => {
    const base = api.defaults.baseURL || '/api';
    return `${base}${FSV}/library/plate-exports/${jobId}/file?token=${encodeURIComponent(localStorage.getItem('accessToken') || '')}`;
  },
};

// A user's own saved prompts, per Program tab or Media AI (`scope`).
export const customPromptService = {
  list: async (scope) => {
    const response = await api.get('/custom-prompts', { params: { scope } });
    return response.data.data;
  },
  create: async (data) => {
    const response = await api.post('/custom-prompts', data);
    return response.data.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/custom-prompts/${id}`, data);
    return response.data.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/custom-prompts/${id}`);
    return response.data.data;
  },
};

// Media AI Service — chat that returns generated images
export const mediaAIService = {
  listThreads: async () => {
    const response = await api.get('/media-ai/threads');
    return response.data.data;
  },

  getThread: async (id) => {
    const response = await api.get(`/media-ai/threads/${id}`);
    return response.data.data;
  },

  deleteThread: async (id) => {
    const response = await api.delete(`/media-ai/threads/${id}`);
    return response.data;
  },

  /** Admin/manager only — every in-scope user's chats (role-scoped server-side). */
  listThreadsForAdmin: async (filters = {}) => {
    const response = await api.get('/media-ai/admin/threads', { params: filters });
    return response.data.data;
  },

  /** Admin/manager only — view one specific user's chat, read-only. */
  getThreadForAdmin: async (id) => {
    const response = await api.get(`/media-ai/admin/threads/${id}`);
    return response.data.data;
  },

  /**
   * Sends a message with optional image/video attachments. Resolves with the
   * thread as soon as the request is accepted — its last message is the
   * reply, still 'processing'; poll getThread until it settles.
   */
  sendMessage: async ({ threadId, message, files = [], imageProvider, aspectRatio, referenceFiles = [], videoModel }) => {
    const form = new FormData();
    if (threadId) form.append('threadId', threadId);
    if (imageProvider) form.append('imageProvider', imageProvider);
    if (videoModel) form.append('videoModel', videoModel);
    if (aspectRatio) form.append('aspectRatio', aspectRatio);
    form.append('message', message || '');
    files.forEach((file) => form.append('files', file));
    referenceFiles.forEach((filename) => form.append('referenceFiles', filename));
    // Uploading a large video can outlast the default request timeout.
    const response = await api.post('/media-ai/messages', form, { timeout: 900000 });
    return response.data.data;
  },

  /**
   * Apply an editor tool to a picture in the chat. Quick edits resolve with
   * the finished thread; AI edits resolve with the reply still 'processing' —
   * poll getThread until it settles, as for a generation.
   */
  editImage: async ({ threadId, filename, tool, params = {}, mask }) => {
    const form = new FormData();
    form.append('filename', filename);
    form.append('tool', tool);
    form.append('params', JSON.stringify(params));
    if (mask) form.append('mask', mask, 'mask.png');
    const response = await api.post(`/media-ai/threads/${threadId}/edit`, form, { timeout: 120000 });
    return response.data.data;
  },

  /** Stop what is being made in this chat. Resolves with the thread. */
  stopGeneration: async (threadId) => {
    const response = await api.post(`/media-ai/threads/${threadId}/stop`);
    return response.data.data;
  },

  /**
   * Edit an earlier message of the user's and send it again (the old version
   * is kept). Resolves with the thread; the new reply is 'processing'.
   */
  editMessage: async ({ threadId, messageId, message, imageProvider, aspectRatio, videoModel }) => {
    const response = await api.post(`/media-ai/threads/${threadId}/messages/${messageId}/edit`, {
      message, imageProvider, aspectRatio, videoModel,
    });
    return response.data.data;
  },

  /** Show another version of an edited message: { group, order } from message.versions. */
  switchVersion: async ({ threadId, group, order }) => {
    const response = await api.post(`/media-ai/threads/${threadId}/versions`, { group, order });
    return response.data.data;
  },

  /** Which providers can make images and videos right now: { image: [{id, name}], video: [...] }. */
  getProviders: async () => {
    const response = await api.get('/media-ai/providers');
    return response.data.data;
  },

  /** Admin only — provider credentials (whether stored, never the values). */
  getSettings: async () => {
    const response = await api.get('/media-ai/settings');
    return response.data.data;
  },

  saveFreepikCredentials: async ({ apiKey, webhookSecret }) => {
    const response = await api.put('/media-ai/settings/freepik', { apiKey, webhookSecret });
    return response.data.data;
  },

  removeFreepikCredentials: async () => {
    const response = await api.delete('/media-ai/settings/freepik');
    return response.data.data;
  },

  // Video providers: google | kling | minimax | alibaba (Alibaba also takes baseUrl).
  saveVideoProviderKey: async (provider, { apiKey, baseUrl }) => {
    const response = await api.put(`/media-ai/settings/video/${provider}`, { apiKey, baseUrl });
    return response.data.data;
  },

  removeVideoProviderKey: async (provider) => {
    const response = await api.delete(`/media-ai/settings/video/${provider}`);
    return response.data.data;
  },

  saveVideoBudget: async (dailyUSD) => {
    const response = await api.put('/media-ai/settings/video-budget', { dailyUSD });
    return response.data.data;
  },

  /** URL for an attached or generated file — token in the query because <img>/<video> can't send headers. */
  fileUrl: (filename, { download = false } = {}) => {
    const base = api.defaults.baseURL || '/api';
    const token = localStorage.getItem('accessToken') || '';
    return `${base}/media-ai/files/${filename}?token=${encodeURIComponent(token)}${download ? '&download=1' : ''}`;
  },
};

// Budget Service
export const budgetService = {
  getBudgetStatus: async () => {
    const response = await api.get('/auth/budget-status');
    return response.data.data;
  },
};

// Prompt Service
export const promptService = {
  listPrompts: async () => {
    const response = await api.get('/prompts');
    return response.data.data;
  },

  getPrompt: async (type) => {
    const response = await api.get(`/prompts/${type}`);
    return response.data.data;
  },

  updatePrompt: async (type, data) => {
    const response = await api.put(`/prompts/${type}`, data);
    return response.data.data;
  },

  deleteOverride: async (type) => {
    const response = await api.delete(`/prompts/${type}/override`);
    return response.data.data;
  },
};

// Spend by user and by activity — admins and managers.
export const costReportService = {
  get: async ({ from, to }) => {
    const response = await api.get('/usage/costs', { params: { from, to } });
    return response.data.data;
  },
};

// IP Whitelist Service (admin only)
export const ipWhitelistService = {
  getSettings: async () => {
    const response = await api.get('/settings/ip-whitelist');
    return response.data.data;
  },
  updateSettings: async (settings) => {
    const response = await api.put('/settings/ip-whitelist', settings);
    return response.data.data;
  },
  getMyIp: async () => {
    const response = await api.get('/settings/my-ip');
    return response.data.data;
  },
};

// Video Service
export const videoService = {
  transcribeUpload: async (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('video', file);
    const response = await api.post('/video/transcribe-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 300000, // 5-min for large files
    });
    return response.data.data;
  },

  transcribeUrl: async (youtubeUrl) => {
    const response = await api.post('/video/transcribe-url', { youtubeUrl }, { timeout: 120000 });
    return response.data.data;
  },

  generateScript: async (transcript, tone, language) => {
    const response = await api.post('/video/generate-script', { transcript, tone, language });
    return response.data.data;
  },

  extractClips: async (youtubeUrl, file, onUploadProgress) => {
    if (youtubeUrl) {
      const response = await api.post('/video/extract-clips', { youtubeUrl }, { timeout: 600000 });
      return response.data.data;
    }
    const formData = new FormData();
    formData.append('video', file);
    const response = await api.post('/video/extract-clips', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 600000,
    });
    return response.data.data;
  },

  getClipDownloadUrl: (jobId, filename) => {
    const base = api.defaults.baseURL || '/api';
    return `${base}/video/clips/${jobId}/${filename}`;
  },
};

// Interview Service
export const interviewService = {
  generateInterview: async (data) => {
    const response = await api.post('/content/generate-interview', data);
    return response.data.data;
  },
};

/**
 * True for a request failure that passes on its own: no answer at all
 * (axios "Network Error", a timeout, the server restarting) or a busy/gateway
 * answer (408, 429, 5xx). Worth retrying; never worth showing to the user.
 */
export const isPassingFailure = (err) => {
  const s = err?.response?.status;
  return !s || s === 408 || s === 429 || s >= 500;
};

// Voice / Script-to-Voice Service
export const voiceService = {
  /** List active voice artists (for user-facing dropdown). */
  listArtists: async () => {
    const response = await api.get('/voice/artists');
    return response.data.data;
  },

  /** List all voices in the connected ElevenLabs account (pre-built + cloned). */
  listElevenLabsVoices: async () => {
    const response = await api.get('/voice/elevenlabs-voices');
    return response.data.data;
  },

  /** Admin: list ALL artists including inactive. */
  listAllArtists: async () => {
    const response = await api.get('/voice/artists/all');
    return response.data.data;
  },

  /**
   * Admin: register a PVC voice artist using an existing ElevenLabs voice ID.
   * The voice was already trained through the ElevenLabs dashboard.
   */
  createArtistFromVoiceId: async ({ name, description, elevenLabsVoiceId }) => {
    const response = await api.post('/voice/artists/from-voice-id', { name, description, elevenLabsVoiceId });
    return response.data.data;
  },

  /**
   * Admin: create a voice artist by uploading audio samples to ElevenLabs (IVC).
   * @param {FormData} formData — must contain `name`, optional `description`, and `files[]`
   * @param {function} onUploadProgress — axios upload progress callback
   */
  createArtist: async (formData, onUploadProgress) => {
    const response = await api.post('/voice/artists', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 300000, // 5 min for large audio uploads
    });
    return response.data.data;
  },

  /** Admin: update voice artist metadata. */
  updateArtist: async (id, data) => {
    const response = await api.put(`/voice/artists/${id}`, data);
    return response.data.data;
  },

  /** Admin: delete a voice artist (removes from ElevenLabs + DB). */
  deleteArtist: async (id) => {
    const response = await api.delete(`/voice/artists/${id}`);
    return response.data;
  },

  /**
   * Start audio generation from script text — returns immediately with a job
   * id; the actual TTS work (chunked, possibly hundreds of ElevenLabs calls
   * for a long script) runs in the background on the server. Poll
   * getGenerationStatus(id) for progress instead of waiting on this call —
   * a 30,000-word script can legitimately take several minutes, far past any
   * sane single-request timeout.
   * Pass artistId (Voice Artist from DB) OR { voiceId, voiceName } (ElevenLabs voice directly).
   */
  startGeneration: async (artistIdOrVoiceObj, script, model) => {
    // One id per press of Generate, sent on every try: if a start request
    // reached the server but its answer was lost, the retry gets that same
    // job back instead of starting (and paying for) the script twice.
    const clientRequestId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    const body = typeof artistIdOrVoiceObj === 'string'
      ? { artistId: artistIdOrVoiceObj, script, model, clientRequestId }
      : { voiceId: artistIdOrVoiceObj.voiceId, voiceName: artistIdOrVoiceObj.voiceName, script, model, clientRequestId };
    for (let attempt = 1; ; attempt++) {
      try {
        const response = await api.post('/voice/generate', body, { timeout: 120000 });
        return response.data.data; // { id, status, totalChunks }
      } catch (err) {
        if (attempt >= 4 || !isPassingFailure(err)) throw err;
        await new Promise((r) => setTimeout(r, 2000 * attempt));
      }
    }
  },

  /** Poll progress for a job started via startGeneration(). */
  getGenerationStatus: async (id) => {
    // silent: a rate-limited poll is just retried by the page, never toasted.
    const response = await api.get(`/voice/generate/${id}/status`, { silent: true, timeout: 30000 });
    return response.data.data; // { status, totalChunks, completedChunks, percentage, filename?, error? }
  },

  /**
   * Fetch a stored/completed audio file as a blob (auth header, not a
   * query-string token). Retries on transient network failures — confirmed
   * live that a single unretried fetch() can fail ("Failed to fetch", a
   * generic browser network error, not an HTTP error status) partway through
   * downloading a large file even though the server served it correctly
   * (200, full Content-Length). A ~900-word script's audio is already ~12MB;
   * a 30,000-word one scales to several hundred MB, so retrying here is what
   * stands between "several minutes of successful generation" and "silently
   * failing at the very last step."
   */
  fetchAudioBlob: async (filename, attempts = 3) => {
    const token = localStorage.getItem('accessToken');
    const base = api.defaults.baseURL || '/api';
    let lastErr;
    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        const resp = await fetch(`${base}/voice/audio/${filename}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error('Failed to load generated audio.');
        return await resp.blob();
      } catch (err) {
        lastErr = err;
        if (attempt < attempts) await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
    }
    throw lastErr;
  },

  /** List user's audio history (newest first). */
  getAudioHistory: async () => {
    const response = await api.get('/voice/history');
    return response.data.data;
  },

  /** Returns the URL to stream a stored audio file (authenticated). */
  getAudioUrl: (filename) => `${api.defaults.baseURL}/voice/audio/${filename}`,

  /** Delete a history entry + its file. */
  deleteAudioHistory: async (id) => {
    const response = await api.delete(`/voice/history/${id}`);
    return response.data;
  },
};

// Dubbing Service (Language Change)
// NOTE: The LanguageChange page uses the `api` axios instance directly
// (multipart/form-data uploads require full axios config control).
// These helpers are kept for use by other components if needed.
export const dubbingService = {
  getStatus: async (jobId) => {
    const response = await api.get(`/dubbing/status/${jobId}`);
    return response.data.data;
  },

  getVideoUrl: (jobId) => {
    const token = localStorage.getItem('accessToken');
    const base  = api.defaults.baseURL || '/api';
    return `${base}/dubbing/video/${jobId}?token=${encodeURIComponent(token)}`;
  },

  listHistory: async () => {
    const response = await api.get('/dubbing/history');
    return response.data.data;
  },

  deleteJob: async (jobId) => {
    const response = await api.delete(`/dubbing/${jobId}`);
    return response.data;
  },
};

// Admin Service
export const adminService = {
  listUsers: async () => {
    const response = await api.get('/auth/admin/users');
    return response.data.data;
  },

  createUser: async (data) => {
    const response = await api.post('/auth/admin/users', data);
    return response.data.data;
  },

  setUserBudget: async (userId, dailyBudget) => {
    const response = await api.put(`/auth/admin/users/${userId}/budget`, { dailyBudget });
    return response.data.data;
  },

  updateUser: async (userId, data) => {
    const response = await api.put(`/auth/admin/users/${userId}`, data);
    return response.data.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/auth/admin/users/${userId}`);
    return response.data.data;
  },

  setUserActive: async (userId, isActive) => {
    const response = await api.put(`/auth/admin/users/${userId}/active`, { isActive });
    return response.data.data;
  },

  /** Admin only — switch Media AI on or off for one user. */
  setUserMediaAI: async (userId, enabled) => {
    const response = await api.put(`/auth/admin/users/${userId}/media-ai`, { enabled });
    return response.data.data;
  },

  /** Admin/manager — switch one opt-in feature (see utils/features.js) on or off for one user. */
  setUserFeature: async (userId, feature, enabled) => {
    const response = await api.put(`/auth/admin/users/${userId}/features/${feature}`, { enabled });
    return response.data.data;
  },
};

// Automation Source Service — input/output source config (read: all users, write: admin only)
export const automationSourceService = {
  listSources: async (type) => {
    const response = await api.get('/automation/sources', { params: type ? { type } : {} });
    return response.data.data;
  },

  createSource: async (data) => {
    const response = await api.post('/automation/sources', data);
    return response.data.data;
  },

  updateSource: async (id, data) => {
    const response = await api.put(`/automation/sources/${id}`, data);
    return response.data.data;
  },

  deleteSource: async (id) => {
    const response = await api.delete(`/automation/sources/${id}`);
    return response.data;
  },
};

// WhatsApp connection config (admin only) — QR-based whatsapp-web.js session
export const whatsAppConfigService = {
  connect: async () => {
    const response = await api.post('/automation/sources/whatsapp/connect');
    return response.data.data;
  },
  getStatus: async () => {
    const response = await api.get('/automation/sources/whatsapp/status');
    return response.data.data;
  },
  listChats: async () => {
    const response = await api.get('/automation/sources/whatsapp/chats');
    return response.data.data;
  },
  updateMonitored: async (monitored) => {
    const response = await api.put('/automation/sources/whatsapp/monitored', monitored);
    return response.data.data;
  },
  disconnect: async () => {
    const response = await api.post('/automation/sources/whatsapp/disconnect');
    return response.data.data;
  },
  sync: async () => {
    const response = await api.post('/automation/sources/whatsapp/sync');
    return response.data.data;
  },
};

// YouTube channel status/disconnect (admin/manager) — `key` is the specific
// channel's AutomationSource.key. Credentials now live on a YouTubeAccount
// (see youtubeAccountService below), not on the channel itself.
export const youtubeConfigService = {
  getStatus: async (key) => {
    const response = await api.get(`/automation/sources/youtube/${key}/status`);
    return response.data.data;
  },
  getRedirectUri: async () => {
    const response = await api.get('/automation/sources/youtube/redirect-uri');
    return response.data.data;
  },
  disconnect: async (key) => {
    const response = await api.post(`/automation/sources/youtube/${key}/disconnect`);
    return response.data.data;
  },
};

// YouTube accounts (admin/manager) — one set of Google OAuth app credentials
// each. A channel is added/reconnected under an account by starting the
// auth-url flow, which redirects to Google and back to the OAuth callback;
// Google's consent screen decides which channel it resolves to.
export const youtubeAccountService = {
  list: async () => {
    const response = await api.get('/automation/sources/youtube/accounts');
    return response.data.data;
  },
  create: async (data) => {
    const response = await api.post('/automation/sources/youtube/accounts', data);
    return response.data.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/automation/sources/youtube/accounts/${id}`, data);
    return response.data.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/automation/sources/youtube/accounts/${id}`);
    return response.data;
  },
  getAuthUrl: async (id) => {
    const response = await api.get(`/automation/sources/youtube/accounts/${id}/auth-url`);
    return response.data.data;
  },
};

// Default source→destination mappings (Settings → Automation Sources) — e.g.
// "this WhatsApp group always publishes to this YouTube channel." Pre-fills
// new Pipeline items only; always overridable per item at publish time.
export const mappingService = {
  list: async (params = {}) => {
    const response = await api.get('/automation/mappings', { params });
    return response.data.data;
  },
  upsert: async (data) => {
    const response = await api.put('/automation/mappings', data);
    return response.data.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/automation/mappings/${id}`);
    return response.data;
  },
};

// Pipeline Service — Automation → Pipeline tab
export const pipelineService = {
  listItems: async (filters = {}, { silent = false } = {}) => {
    const response = await api.get('/automation/pipeline', { params: filters, silent });
    return response.data.data;
  },

  // Admin-only, rerunnable — backfills groupName/contactNumber on items captured
  // before the Group/Contact filters existed (see PipelineController for details).
  backfillMonitoredFields: async () => {
    const response = await api.post('/automation/pipeline/backfill-monitored');
    return response.data.data;
  },

  // Attachment streaming route sits outside the normal auth flow (opened via
  // <a>/<img> which can't set an Authorization header) — token goes in the query
  // string instead, same pattern as dubbingService.getVideoUrl / voiceService.getAudioUrl.
  getAttachmentUrl: (filename) => {
    const token = localStorage.getItem('accessToken');
    const base = api.defaults.baseURL || '/api';
    return `${base}/automation/pipeline/attachments/${filename}?token=${encodeURIComponent(token)}`;
  },

  // Server-Sent Events stream — pushes a notification the instant a message is
  // captured, so the table can refetch immediately instead of waiting for the
  // next poll. Same query-token pattern as getAttachmentUrl (EventSource can't
  // set an Authorization header either).
  getEventsUrl: () => {
    const token = localStorage.getItem('accessToken');
    const base = api.defaults.baseURL || '/api';
    return `${base}/automation/pipeline/events?token=${encodeURIComponent(token)}`;
  },

  createItem: async (data) => {
    const response = await api.post('/automation/pipeline', data);
    return response.data.data;
  },

  getItem: async (id) => {
    const response = await api.get(`/automation/pipeline/${id}`);
    return response.data.data;
  },

  updateItem: async (id, data) => {
    const response = await api.put(`/automation/pipeline/${id}`, data);
    return response.data.data;
  },

  // Kicks off narration generation (script sized to the video → TTS via the
  // chosen Voice Artist → muxed onto the video). Not instant — poll getItem()
  // for voiceOver.status until 'completed'/'failed'.
  startVoiceOver: async (id, data) => {
    const response = await api.post(`/automation/pipeline/${id}/voiceover`, data);
    return response.data.data;
  },

  // Stops a running generation — marks it failed/cancelled immediately even
  // though the background work may keep running harmlessly until its own
  // server-side timeout ends it (see PipelineVoiceOverService).
  cancelVoiceOver: async (id) => {
    const response = await api.post(`/automation/pipeline/${id}/voiceover/cancel`);
    return response.data.data;
  },

  // Skip AI script + TTS — mux the admin's own pre-recorded narration file onto
  // the selected video instead. Used to respond synchronously ("fast, no
  // AI/TTS round trip") — no longer true now that the video itself can be up
  // to 3h long (the mux+watermark step now runs as a background job, same as
  // generated narration — poll getItem() for voiceOver.status). This request
  // only needs enough time for the audio *file* to finish uploading, which is
  // small and fast regardless of video length — the shared default timeout
  // (see utils/api.js) is already generous for that.
  uploadVoiceOverAudio: async (id, attachmentFilename, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('attachmentFilename', attachmentFilename);
    const response = await api.post(`/automation/pipeline/${id}/voiceover/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data.data;
  },

  // Upload/replace a video (or other media) directly, e.g. swapping in a
  // higher-quality file than what WhatsApp compressed on the way in.
  uploadAttachment: async (id, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/automation/pipeline/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      // A ~30-min source video is a much larger upload than this was
      // originally sized for — give the transfer itself real headroom.
      timeout: 20 * 60 * 1000,
    });
    return response.data.data;
  },

  removeAttachment: async (id, filename) => {
    const response = await api.delete(`/automation/pipeline/${id}/attachments/${filename}`);
    return response.data.data;
  },

  // order is an array of attachment filenames in the desired merge sequence —
  // sets each one's 1-based position; any video attachment omitted has its
  // sequence cleared (not included in the next merge).
  updateAttachmentSequence: async (id, order) => {
    const response = await api.put(`/automation/pipeline/${id}/attachments/sequence`, { order });
    return response.data.data;
  },

  // Kicks off a background merge (see PipelineController.mergeVideos /
  // PipelineVideoMergeService.runMergeJob) — responds almost instantly
  // (202) regardless of how long the actual ffmpeg run takes, so no custom
  // timeout is needed here anymore (matches startVoiceOver's shape). Poll
  // getItem() for merge.status until 'completed'/'failed'.
  mergeVideos: async (id, filenames) => {
    const response = await api.post(`/automation/pipeline/${id}/merge-videos`, { filenames });
    return response.data.data;
  },

  // Generates a few candidate thumbnails (video frame + bold text overlay).
  // Fast — no job/polling needed.
  generateThumbnails: async (id, data) => {
    const response = await api.post(`/automation/pipeline/${id}/thumbnails/generate`, data, { timeout: 60000 });
    return response.data.data;
  },

  // AI-generated background image (content-relevant, not a video frame) + the
  // same text overlay treatment. Two AI calls happen server-side (image-prompt
  // writer, then the actual image generation) — gpt-image-1 can legitimately
  // take up to ~4 minutes (the backend allows up to 240s for that call
  // specifically), so this needs real margin above that. The old 120s here
  // was shorter than the backend's own call could legitimately take, which is
  // exactly what caused "it just loads and returns nothing."
  generateThumbnailsAI: async (id, data) => {
    const response = await api.post(`/automation/pipeline/${id}/thumbnails/generate-ai`, data, { timeout: 270000 });
    return response.data.data;
  },

  // Repositions/resizes the thumbnail text without regenerating the
  // background — cheap (a recomposite, not a video-frame or OpenAI call), so
  // this can be called on every arrow/size-control click.
  updateThumbnailStyle: async (id, data) => {
    const response = await api.post(`/automation/pipeline/${id}/thumbnails/style`, data, { timeout: 20000 });
    return response.data.data;
  },

  // Upload a custom thumbnail image instead of (or in addition to) the
  // auto-generated candidates — added to the same candidate list, selected immediately.
  uploadThumbnail: async (id, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/automation/pipeline/${id}/thumbnails/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 60000,
    });
    return response.data.data;
  },

  // For the YouTube destination, this now kicks off a background job (see
  // PipelineController.publishItem / PipelinePublishService.runPublish) —
  // responds almost instantly (202) regardless of watermarking/upload time,
  // so no custom timeout is needed anymore. Poll getItem() for
  // publish.status until 'completed'/'failed'. Non-YouTube destinations have
  // no upload step and still complete synchronously in this same call.
  publishItem: async (id, data) => {
    const response = await api.post(`/automation/pipeline/${id}/publish`, data);
    return response.data.data;
  },
};

// Change overs: 3D stings between two shots, made from a prompt (+ references) and edited in a studio.
const CO = '/changeovers';
const coToken = () => encodeURIComponent(localStorage.getItem('accessToken') || '');
export const changeoverService = {
  options: () => fsData(api.get(`${CO}/options`)),
  list: () => fsData(api.get(CO)),
  get: (id) => fsData(api.get(`${CO}/${id}`)),
  create: ({ prompt, name, tags, files = [], ranges = [] }, onUploadProgress) => {
    const form = new FormData();
    form.append('prompt', prompt);
    if (name) form.append('name', name);
    if (tags?.length) form.append('tags', tags.join(','));
    form.append('ranges', JSON.stringify(ranges));
    files.forEach((f) => form.append('files', f));
    return fsData(api.post(CO, form, { timeout: 600000, onUploadProgress }));
  },
  patch: (id, data) => fsData(api.patch(`${CO}/${id}`, data)),
  save: (id, spec, note) => fsData(api.post(`${CO}/${id}/save`, { spec, note })),
  ai: (id, prompt, spec) => fsData(api.post(`${CO}/${id}/ai`, { prompt, spec })),
  restore: (id, n) => fsData(api.post(`${CO}/${id}/versions/${n}/restore`)),
  retry: (id) => fsData(api.post(`${CO}/${id}/retry`)),
  duplicate: (id) => fsData(api.post(`${CO}/${id}/duplicate`)),
  remove: (id) => fsData(api.delete(`${CO}/${id}`)),
  prompts: () => fsData(api.get(`${CO}/prompts`)),
  addFavorite: (label, prompt) => fsData(api.post(`${CO}/prompts/favorites`, { label, prompt })),
  relabelFavorite: (favId, data) => fsData(api.patch(`${CO}/prompts/favorites/${favId}`, data)),
  removeFavorite: (favId) => fsData(api.delete(`${CO}/prompts/favorites/${favId}`)),
  /** The preview video or poster — the token goes in the URL for <video>/<img>. */
  fileUrl: (id, kind, version, { download = false } = {}) => `${api.defaults.baseURL || '/api'}${CO}/${id}/${kind}?token=${coToken()}&v=${version || 0}${download ? '&download=1' : ''}`,
  refUrl: (id, file) => `${api.defaults.baseURL || '/api'}${CO}/${id}/refs/${file}?token=${coToken()}`,
  /** A finished file to download: mov (ProRes 4444, transparent) / webm (VP9, transparent) / mp4 (preview). */
  downloadUrl: (id, format, version) => `${api.defaults.baseURL || '/api'}${CO}/${id}/download/${format}?token=${coToken()}&v=${version || 0}`,
  claimDraw: (id, token) => fsData(api.post(`${CO}/${id}/draw/claim`, { token })),
  /** What this browser drew: colour + alpha (and an intro, a poster) as MP4/JPEG buffers. */
  uploadDrawing: (id, d, onUploadProgress) => {
    const form = new FormData();
    form.append('token', d.token);
    form.append('version', String(d.version));
    form.append('timeline', JSON.stringify(d.timeline));
    form.append('introFrames', String(d.introFrames || 0));
    form.append('drawMs', String(d.drawMs || 0));
    form.append('colour', new Blob([d.colour], { type: 'video/mp4' }), 'colour.mp4');
    form.append('alpha', new Blob([d.alpha], { type: 'video/mp4' }), 'alpha.mp4');
    if (d.intro) form.append('intro', new Blob([d.intro], { type: 'video/mp4' }), 'intro.mp4');
    if (d.poster) form.append('poster', new Blob([d.poster], { type: 'image/jpeg' }), 'poster.jpg');
    return fsData(api.post(`${CO}/${id}/draw`, form, { timeout: 600000, onUploadProgress }));
  },
  /** The live preview page (public stage files, shown in an iframe). */
  liveUrl: () => `${api.defaults.baseURL || '/api'}${CO}/live/index.html`,
};
